import React from "react";
import Avatar from "src/components/Avatar";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Loader from "src/components/Loader";
import Modal from "src/components/Modal";
import ResumeBadges from "src/components/resume/modules/ResumeBadges";
import ResumeExperiencesList from "src/components/resume/modules/ResumeExperiencesList";
import ResumeReviews from "src/components/resume/modules/ResumeReviews";
import ResumeSkills from "src/components/resume/modules/ResumeSkills";
import ResumeSocialMedia from "src/components/resume/modules/ResumeSocialMedia";
import RegistrationDetails from "src/components/tasks/panes/registration/RegistrationDetails";
import useGet from "src/hooks/rest/useGet";

interface RegistrationDetailModalProps extends Translation {
	registrationId: number | string;
	modal: {
		visible: boolean;
		onClose: () => void;
	};
}

const RegistrationDetailModal = ({
	t,
	registrationId,
	modal,
}: RegistrationDetailModalProps) => {
	const [{ data: registration }, loading] = useGet(
		`/company/registrations/${registrationId}`
	);
	const color = registration?.user?.primary_color;
	return (
		<Modal
			head="floating"
			onClose={modal.onClose}
			visible={modal.visible}
			size="sidebar"
		>
			{loading ? (
				<div className="flex flex-col justify-center items-center">
					<Loader />
				</div>
			) : (
				registration && (
					<>
						<div className="-mx-8 -mt-8 mb-8">
							<div
								style={{
									backgroundColor: color,
								}}
								className="p-8 py-16"
							></div>
							<div className="px-8 -mt-12">
								<div className="flex gap-4 items-end">
									<Avatar
										size="large"
										src={registration.user.avatar}
									/>
									<h3 className="text-left text-xxl">
										{registration.user.full_name}
									</h3>
								</div>
							</div>
						</div>
						<div className="flex flex-col gap-6 text-left">
							<RegistrationDetails
								variant="vertical"
								{...{ registration }}
							/>
							<div className="my-4"></div>

							<Section title={t("sections.education")}>
								<ResumeExperiencesList
									className=""
									type="education"
									query={`/resume/${registration.user.id}/experiences?type=education`}
									{...{ color }}
								/>
							</Section>
							<Line />

							<Section title={t("sections.job")}>
								<ResumeExperiencesList
									className=""
									type="job"
									query={`/resume/${registration.user.id}/experiences?type=education`}
									{...{ color }}
								/>
							</Section>
							<Line />

							<Section title={t("sections.skills")}>
								<ResumeSkills
									query={`/resume/${registration.user.id}/skills`}
								/>
							</Section>
							<Line />

							<Section title={t("sections.social-media")}>
								<ResumeSocialMedia
									query={`/resume/${registration.user.id}/social-media`}
								/>
							</Section>
							<Line />

							<Section title={t("sections.badges")}>
								<ResumeBadges
									query={`/resume/${registration.user.id}/badges`}
								/>
							</Section>
							<Line />

							<Section title={t("sections.reviews")}>
								<ResumeReviews
									options={{
										scrollToTop: false,
									}}
									query={`/resume/${registration.user.id}/reviews`}
								/>
							</Section>
						</div>
					</>
				)
			)}
		</Modal>
	);
};

RegistrationDetailModal.locale = {
	nl: {
		title: "Aanmelding",
		sections: {
			education: "Opleidingen & cursussen",
			job: "Werkervaring",
			skills: "Vaardigheden",
			"social-media": "Social media",
			badges: "Badges",
			reviews: "Reviews",
		},
	},
};

interface SectionProps {
	title: string;
	children?: any;
}

const Section = ({ title, children }: SectionProps) => {
	return (
		<div className="flex flex-col gap-4">
			<h3>{title}</h3>
			<div className="">{children}</div>
		</div>
	);
};

const Line = () => (
	<hr className="border-gray-dark border-1 opacity-60 my-4 text-left" />
);

export default withTranslation(RegistrationDetailModal);
